/**
 * Local Stories — editorial human-interest content about Australian SMBs.
 * Deliberately a separate collection from the blog: stories never appear in
 * the blog feed and blog posts never appear in /stories.
 */

export type StoryBlock =
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "quote"; text: string; attribution?: string }
  | { type: "image"; src: string; alt?: string; caption?: string }
  | { type: "video"; url: string; title?: string };

export interface StoryGalleryImage {
  src: string;
  alt?: string;
  caption?: string;
}

export interface StoryVideo {
  url: string;
  title?: string;
}

export type StoryStatus = "draft" | "scheduled" | "published";

export interface LocalStory {
  id: string;
  slug: string;
  status: StoryStatus;
  headline: string;
  standfirst: string | null;
  authorName: string | null;
  authorBio: string | null;
  publishedAt: string | null;
  updatedAt: string | null;
  businessName: string | null;
  town: string | null;
  state: string | null;
  regionSlug: string | null;
  industry: string | null;
  theme: string | null;
  heroImage: string | null;
  heroImageAlt: string | null;
  gallery: StoryGalleryImage[];
  videos: StoryVideo[];
  body: StoryBlock[];
  businessUrl: string | null;
  /** Internal only — never rendered as a label. */
  isCustomer: boolean;
  relatedCaseStudySlug: string | null;
  featured: boolean;
  seoTitle?: string;
  seoDescription?: string;
}

export const STORY_THEMES = [
  "Family handover",
  "Second act",
  "Last of their kind",
  "Against the odds",
  "Built for the town",
  "New in town",
] as const;

export const STORY_STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"] as const;

export const MEDIA_CONTACT_EMAIL = "marketing@localsearch.com.au";

export const NOMINATION_STATUSES = [
  "new",
  "contacted",
  "in_progress",
  "published",
  "declined",
] as const;
export type NominationStatus = (typeof NOMINATION_STATUSES)[number];

export const NOMINATION_STATUS_LABELS: Record<NominationStatus, string> = {
  new: "New",
  contacted: "Contacted",
  in_progress: "In progress",
  published: "Published",
  declined: "Declined",
};

export const STORY_RELATIONSHIPS = ["Owner", "Customer", "Staff", "Other"] as const;
